import { User as FirebaseUser } from 'firebase/auth';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import type { AdminProfile } from '../contexts/AuthContext';
import { db } from '../lib/firebase';

const EMAIL_API_ALLOW_FALLBACK = String(process.env.REACT_APP_EMAIL_API_ALLOW_FALLBACK || '').toLowerCase() === 'true';
const EMAIL_API_BASE_URLS = (() => {
  const primaryUrl = (process.env.REACT_APP_EMAIL_API_URL || '').trim();
  const fallbackUrls = [
    process.env.REACT_APP_ADMIN_SERVER_URL,
    process.env.REACT_APP_WEBSOCKET_URL,
    'http://localhost:3001'
  ];

  const candidates = EMAIL_API_ALLOW_FALLBACK
    ? [primaryUrl, ...fallbackUrls]
    : [primaryUrl || 'http://localhost:3001'];

  return Array.from(new Set(
    candidates
      .filter((value): value is string => Boolean(value && value.trim()))
      .map((value) => value.replace(/\/+$/, ''))
  ));
})();

const HISTORY_LIMIT = 12;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface EmailSenderOption {
  email: string;
  label: string;
  name: string;
}

export interface EmailRecipientPreview {
  id: string;
  displayName: string;
  email: string;
  state: string;
  city: string;
  location: string;
  role?: string;
  sports?: string[];
  language?: string;
  isVerified?: boolean;
  createdAt?: string | null;
  isNewUser?: boolean;
  isVerifier?: boolean;
}

export interface EmailRecipientFilters {
  includeNotVerified: boolean;
  includeNewUsers: boolean;
  includeVerifiers: boolean;
  sports?: string[];
  accountTypes?: string[];
  locations?: string[];
  languages?: string[];
}

export type EmailRecipientMode = 'all' | 'single' | 'selected';

export type EmailLanguage = 'en' | 'hi';

export const DEFAULT_EMAIL_LANGUAGE: EmailLanguage = 'en';

export const EMAIL_LANGUAGE_OPTIONS: Array<{ value: EmailLanguage; label: string; nativeLabel: string }> = [
  { value: 'en', label: 'English', nativeLabel: 'English' },
  { value: 'hi', label: 'Hindi', nativeLabel: 'हिन्दी' }
];

export const normalizeEmailLanguage = (value: any): EmailLanguage => {
  const normalized = String(value || '').trim().toLowerCase().split(/[-_]/)[0];
  return normalized === 'hi' ? 'hi' : DEFAULT_EMAIL_LANGUAGE;
};

// The recipient's own language_preference (set in the main app), which is distinct
// from EMAIL_LANGUAGE_OPTIONS above — that one picks which template to send.
// Users who never set a preference fall into the 'unknown' bucket so they stay
// selectable. Must stay in sync with normalizeRecipientLanguage in emailApi.js.
export const UNKNOWN_RECIPIENT_LANGUAGE = 'unknown';

export const RECIPIENT_LANGUAGE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi (हिन्दी)' },
  { value: UNKNOWN_RECIPIENT_LANGUAGE, label: 'Not Specified' }
];

export const normalizeRecipientLanguage = (value: any): string => {
  const normalized = String(value || '').trim().toLowerCase().split(/[-_]/)[0];
  return normalized || UNKNOWN_RECIPIENT_LANGUAGE;
};

export interface EmailSportImageOption {
  value: string;
  label: string;
  labelHi: string;
  url: string;
}

// Example profile cards available to the profile-completion templates.
// Keep in sync with SPORT_PROFILE_IMAGES in performance-server/emailApi.js.
export const SPORT_IMAGE_OPTIONS: EmailSportImageOption[] = [
  { value: 'shooting', label: 'Shooting', labelHi: 'शूटिंग', url: 'https://media.amaplayer.com/email_media/WhatsApp%20Image%202026-09-03%20at%2012.26.25%20PM.jpeg' },
  { value: 'basketball', label: 'Basketball', labelHi: 'बास्केटबॉल', url: 'https://media.amaplayer.com/email_media/WhatsApp%20Image%202026-09-03%20at%2012.26.26%20PM.jpeg' },
  { value: 'volleyball', label: 'Volleyball', labelHi: 'वॉलीबॉल', url: 'https://media.amaplayer.com/email_media/WhatsApp%20Image%202026-09-03%20at%2012.26.27%20PM(1).jpeg' },
  { value: 'athletics', label: 'Athletics', labelHi: 'एथलेटिक्स', url: 'https://media.amaplayer.com/email_media/WhatsApp%20Image%202026-09-03%20at%2012.26.27%20PM.jpeg' },
  { value: 'badminton', label: 'Badminton', labelHi: 'बैडमिंटन', url: 'https://media.amaplayer.com/email_media/WhatsApp%20Image%202026-09-03%20at%2012.26.28%20PM(1).jpeg' },
  { value: 'table-tennis', label: 'Table Tennis', labelHi: 'टेबल टेनिस', url: 'https://media.amaplayer.com/email_media/WhatsApp%20Image%202026-09-03%20at%2012.26.28%20PM.jpeg' },
  { value: 'football', label: 'Football', labelHi: 'फ़ुटबॉल', url: 'https://media.amaplayer.com/email_media/WhatsApp%20Image%202026-10-07%20at%2010.07.45%20AM.jpeg' }
];

// Templates that render an embedded example profile card.
export const SPORT_IMAGE_TEMPLATE_IDS = ['complete-profile-player', 'complete-profile-parent'];

export const normalizeSportImageKey = (value: any): string => {
  const normalized = String(value || '').trim().toLowerCase();
  return SPORT_IMAGE_OPTIONS.some((option) => option.value === normalized) ? normalized : '';
};

export interface EmailContextResponse {
  role: 'superAdmin' | 'admin' | 'employee';
  senderOptions: EmailSenderOption[];
  supportedLanguages?: EmailLanguage[];
  sportImageOptions?: EmailSportImageOption[];
  sportImageTemplateIds?: string[];
  audienceDescription: string;
  recentSends?: EmailSendHistoryEntry[];
  selectableRecipients?: EmailRecipientPreview[];
  recipientSummary: {
    totalCount: number;
    scope: 'all_users' | 'assigned_regions';
    regions: string[];
    truncated: boolean;
    users: EmailRecipientPreview[];
  };
}

export interface EmailSendHistoryEntry {
  id: string;
  adminUid: string;
  adminEmail: string;
  adminDisplayName: string;
  adminRole: string;
  sender: string;
  subject: string;
  language: EmailLanguage;
  sportImage: string;
  recipientMode: EmailRecipientMode;
  recipientFilters: EmailRecipientFilters;
  customRecipientCount: number;
  sentCount: number;
  batchCount: number;
  sampleRecipients: string[];
  scope: 'all_users' | 'assigned_regions';
  regions: string[];
  createdAt: string | null;
}

export interface SendBulkEmailInput {
  senderEmail: string;
  subject: string;
  body: string;
  templateId?: string;
  language?: EmailLanguage;
  sportImage?: string;
  enableRecipientPersonalization?: boolean;
  recipientMode?: EmailRecipientMode;
  recipientIds?: string[];
  recipientFilters?: Partial<EmailRecipientFilters>;
  customRecipientEmails?: string[];
  recipientPersonalization?: Array<{
    id?: string;
    email: string;
    displayName?: string;
  }>;
}

export interface SendBulkEmailResponse {
  sender: string;
  sentCount: number;
  batchCount: number;
  scope: 'all_users' | 'assigned_regions';
  language?: EmailLanguage;
  sportImage?: string;
  recipientMode?: EmailRecipientMode;
  recipientFilters?: EmailRecipientFilters;
  customRecipientCount?: number;
  trackingId?: string;
  trackingWarning?: string;
}

const normalizeEmail = (value = '') => value.trim().toLowerCase();

const parseDateToIso = (value: any): string | null => {
  if (!value) return null;

  if (typeof value === 'object') {
    if (typeof value.toDate === 'function') {
      return value.toDate().toISOString();
    }

    const secs = typeof value._seconds === 'number' ? value._seconds : (typeof value.seconds === 'number' ? value.seconds : null);
    if (secs !== null) {
      const date = new Date(secs * 1000);
      return Number.isNaN(date.getTime()) ? null : date.toISOString();
    }

    if (value.timestampValue) {
      const date = new Date(value.timestampValue);
      return Number.isNaN(date.getTime()) ? null : date.toISOString();
    }
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

export const parseCustomRecipientEmails = (input: string): { valid: string[]; invalid: string[] } => {
  const values = String(input || '')
    .split(/[\n,;]+/)
    .map((value) => normalizeEmail(value))
    .filter(Boolean);

  const unique = Array.from(new Set(values));
  const valid = unique.filter((email) => EMAIL_PATTERN.test(email));
  const invalid = unique.filter((email) => !EMAIL_PATTERN.test(email));

  return { valid, invalid };
};

const parseErrorMessage = async (response: Response): Promise<string> => {
  try {
    const payload = await response.json();
    return payload?.error || payload?.message || `Request failed with status ${response.status}`;
  } catch (error) {
    return `Request failed with status ${response.status}`;
  }
};

const mapEmailHistoryEntry = (value: any): EmailSendHistoryEntry | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const createdAt = parseDateToIso(value.createdAt);

  return {
    id: String(value.id || '').trim(),
    adminUid: String(value.adminUid || '').trim(),
    adminEmail: normalizeEmail(value.adminEmail || ''),
    adminDisplayName: String(value.adminDisplayName || '').trim(),
    adminRole: String(value.adminRole || '').trim(),
    sender: normalizeEmail(value.sender || ''),
    subject: String(value.subject || '').trim(),
    language: normalizeEmailLanguage(value.language),
    sportImage: normalizeSportImageKey(value.sportImage),
    recipientMode: value.recipientMode === 'single' || value.recipientMode === 'selected' ? value.recipientMode : 'all',
    recipientFilters: {
      includeNotVerified: Boolean(value.recipientFilters?.includeNotVerified),
      includeNewUsers: Boolean(value.recipientFilters?.includeNewUsers),
      includeVerifiers: Boolean(value.recipientFilters?.includeVerifiers),
      sports: Array.isArray(value.recipientFilters?.sports) ? value.recipientFilters.sports : [],
      accountTypes: Array.isArray(value.recipientFilters?.accountTypes) ? value.recipientFilters.accountTypes : [],
      locations: Array.isArray(value.recipientFilters?.locations) ? value.recipientFilters.locations : [],
      languages: Array.isArray(value.recipientFilters?.languages) ? value.recipientFilters.languages : []
    },
    customRecipientCount: Number(value.customRecipientCount || 0),
    sentCount: Number(value.sentCount || 0),
    batchCount: Number(value.batchCount || 0),
    sampleRecipients: Array.isArray(value.sampleRecipients)
      ? value.sampleRecipients.map((entry: string) => normalizeEmail(entry)).filter(Boolean).slice(0, 5)
      : [],
    scope: value.scope === 'assigned_regions' ? 'assigned_regions' : 'all_users',
    regions: Array.isArray(value.regions)
      ? value.regions.map((entry: string) => String(entry || '').trim()).filter(Boolean)
      : [],
    createdAt
  };
};

const createHeaders = async (currentUser: FirebaseUser): Promise<HeadersInit> => {
  const token = await currentUser.getIdToken();

  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`
  };
};

class EmailService {
  private async getRecentHistory(adminProfile?: AdminProfile | null): Promise<EmailSendHistoryEntry[]> {
    if (!db) {
      return [];
    }

    try {
      const adminUid = String(adminProfile?.uid || '').trim();
      const historyQuery = adminProfile?.role === 'superAdmin'
        ? query(
            collection(db, 'email_send_history'),
            orderBy('createdAt', 'desc'),
            limit(HISTORY_LIMIT)
          )
        : query(
            collection(db, 'email_send_history'),
            where('adminUid', '==', adminUid),
            orderBy('createdAt', 'desc'),
            limit(HISTORY_LIMIT)
          );
      const snapshot = await getDocs(historyQuery);

      const entries = snapshot.docs
        .map((document) => mapEmailHistoryEntry({ id: document.id, ...document.data() }))
        .filter(Boolean) as EmailSendHistoryEntry[];

      if (adminProfile?.role === 'superAdmin') {
        return entries.slice(0, HISTORY_LIMIT);
      }

      return entries
        .filter((entry) => entry.adminUid === adminUid)
        .slice(0, HISTORY_LIMIT);
    } catch (error) {
      console.warn('[EmailService] Failed to load email send history from Firestore:', error);
      return [];
    }
  }

  private async requestJson<T>(
    currentUser: FirebaseUser,
    path: string,
    init: RequestInit
  ): Promise<T> {
    const headers = await createHeaders(currentUser);
    const requestInit: RequestInit = {
      ...init,
      headers: {
        ...headers,
        ...(init.headers || {})
      }
    };

    // Always prioritize configured order (local first) so env updates on local API
    // are picked up even if a remote fallback succeeded earlier in this session.
    const candidateBaseUrls = EMAIL_API_BASE_URLS;

    let lastError: Error | null = null;

    for (const baseUrl of candidateBaseUrls) {
      try {
        const response = await fetch(`${baseUrl}${path}`, requestInit);
        const contentType = (response.headers.get('content-type') || '').toLowerCase();
        const isJson = contentType.includes('application/json');

        if (!response.ok) {
          if (isJson) {
            throw new Error(await parseErrorMessage(response));
          }

          throw new Error(`Email API at ${baseUrl} returned ${response.status} (${contentType || 'unknown content type'}).`);
        }

        if (!isJson) {
          throw new Error(`Email API at ${baseUrl} returned non-JSON content (${contentType || 'unknown'}).`);
        }

        return response.json();
      } catch (error: any) {
        lastError = error instanceof Error ? error : new Error(String(error));
      }
    }

    throw lastError || new Error('Unable to reach a valid Email API endpoint.');
  }

  async getContext(currentUser: FirebaseUser, adminProfile?: AdminProfile | null): Promise<EmailContextResponse> {
    const context = await this.requestJson<EmailContextResponse>(currentUser, '/api/email/context', {
      method: 'GET'
    });

    const recentSends = Array.isArray(context.recentSends) && context.recentSends.length > 0
      ? context.recentSends.map(mapEmailHistoryEntry).filter(Boolean) as EmailSendHistoryEntry[]
      : await this.getRecentHistory(adminProfile);

    return {
      ...context,
      recentSends,
      selectableRecipients: context.selectableRecipients || context.recipientSummary?.users || []
    };
  }

  async sendBulkEmail(currentUser: FirebaseUser, input: SendBulkEmailInput): Promise<SendBulkEmailResponse> {
    try {
      return await this.requestJson<SendBulkEmailResponse>(currentUser, '/api/email/send', {
        method: 'POST',
        body: JSON.stringify(input)
      });
    } catch (error: any) {
      const message = error instanceof Error ? error.message : String(error);
      if (
        message.includes('non-JSON content') ||
        message.includes('Unable to reach a valid Email API endpoint') ||
        message.includes('returned 404')
      ) {
        throw new Error('Email API backend is not reachable.');
      }

      throw error;
    }
  }
}

export const emailService = new EmailService();
